import {
  ShoppingPreferences,
  PreferencesWithoutTimestamp,
  SyncResult,
  StorageToClear,
} from '@lib/shopping-preferences/types';
import { withTimestamp, createDefaultPreferences } from '@helpers/shopping-preferences/defaults';
import { validatePreferencesPayload, hasUserSelectedPreferences } from '@helpers/shopping-preferences/validation';
import { updateHomepageWithPreferences } from '@helpers/shopping-preferences/homepage-preferences-manager';
import logger from '@helpers/utils/logger/client';
import { persistPreferences, clearPersistedPreferences } from '@lib/shopping-preferences/sync-strategy';
import { syncPreferencesV2 } from './sync-strategy-v2';

export const initializePreferencesV2 = (
  backendPrefs: PreferencesWithoutTimestamp | null,
  isAuthenticated: boolean,
  clearTemporaryOnSync: boolean = false,
): SyncResult => {
  logger.info('[Shopping Prefs Manager v2] Initializing preferences', {
    hasBackendPrefs: !!backendPrefs,
    isAuthenticated,
    clearTemporaryOnSync,
  });

  return syncPreferencesV2(backendPrefs, clearTemporaryOnSync);
};

export const updatePreferencesWithSyncV2 = (
  newPrefs: PreferencesWithoutTimestamp,
  isAuthenticated: boolean,
): { preferences: ShoppingPreferences; shouldSyncBackend: boolean } => {
  const validation = validatePreferencesPayload(newPrefs);
  if (!validation.isValid) {
    logger.error('[Shopping Prefs Manager v2] Invalid preferences update', validation.errors);
    throw new Error(`Invalid preferences: ${validation.errors?.join(', ')}`);
  }

  const updated = withTimestamp(newPrefs);
  const persistSuccess = persistPreferences(updated, isAuthenticated, false);
  if (!persistSuccess) {
    logger.error('[Shopping Prefs Manager v2] Failed to persist preferences');
    throw new Error('Failed to persist preferences to storage');
  }

  logger.info('[Shopping Prefs Manager v2] Preferences updated successfully', {
    universe: updated.universe,
    isAuthenticated,
  });

  return {
    preferences: updated,
    shouldSyncBackend: isAuthenticated,
  };
};

export const clearPreferencesV2 = (storageToClear: StorageToClear): ShoppingPreferences => {
  logger.info('[Shopping Prefs Manager v2] Clearing preferences', { storage: storageToClear });
  clearPersistedPreferences(storageToClear);
  return createDefaultPreferences();
};

export const setSessionOnlyPreferencesV2 = (newPrefs: PreferencesWithoutTimestamp): ShoppingPreferences => {
  const validation = validatePreferencesPayload(newPrefs);
  if (!validation.isValid) {
    logger.error('[Shopping Prefs Manager v2] Invalid session preferences', validation.errors);
    throw new Error(`Invalid preferences: ${validation.errors?.join(', ')}`);
  }

  const updated = withTimestamp(newPrefs);
  const persistSuccess = persistPreferences(updated, false, true);
  if (!persistSuccess) {
    logger.error('[Shopping Prefs Manager v2] Failed to persist session preferences');
    throw new Error('Failed to persist session preferences');
  }

  updateHomepageWithPreferences(updated);

  logger.info('[Shopping Prefs Manager v2] Session preferences set', {
    universe: updated.universe,
  });

  return updated;
};

export const checkUserSelectionV2 = (preferences: ShoppingPreferences | null): boolean => {
  return hasUserSelectedPreferences(preferences);
};

export const updateHomepageAfterPreferencesChangeV2 = (preferences: PreferencesWithoutTimestamp): void => {
  updateHomepageWithPreferences(preferences);
};
